import { Fragment, useEffect, useRef, useState } from "react";
import {
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faChevronUp,
    faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Form, Table } from "react-bootstrap";
import MISTooltip from "../../../../components/tooltip";
import { ServiceManager } from "../../../../services/ServiceManager";
import AttendanceStatusChip from "./attendance-status-chip";
import AttendanceUkDateInput from "./attendance-uk-date-input";
import { toIsoDateFromUkDate, toUkDateFromIsoDate } from "./attendance-utils";
import type {
    AttendanceManagementCommandStatusFilter,
    IAttendanceManagementCommandHistoryRow,
    IAttendanceRequestError,
    IAttendanceRequestFieldError,
} from "../../../../models/AttendanceManagement";

export interface ICommandHistoryFilterInput {
    fromDate: string;
    toDate: string;
    class: string;
    status: "" | AttendanceManagementCommandStatusFilter;
    search: string;
}

interface IManagementCommandHistoryProps {
    appliedFilters: ICommandHistoryFilterInput;
    error: IAttendanceRequestError | null;
    filters: ICommandHistoryFilterInput;
    isLoading: boolean;
    onApply: (filters: ICommandHistoryFilterInput) => void;
    onFiltersChange: (filters: ICommandHistoryFilterInput) => void;
    onPageChange: (page: number) => void;
    page: number;
    pageSize: number;
    rows: IAttendanceManagementCommandHistoryRow[];
    totalCount: number | null;
    validationErrors: IAttendanceRequestFieldError[];
}

type CommandDetailKind = "result" | "more-details";

const minimumSearchLength = 3;

interface IExpandedCommandDetail {
    rowId: string;
    kind: CommandDetailKind;
}

function formatManagementTimestamp(value: string): string {
    const timestamp = new Date(value);
    const time = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: "UTC",
    }).format(timestamp);
    const date = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        timeZone: "UTC",
        year: "numeric",
    }).format(timestamp);

    return `${time} · ${date}`;
}

function MissingApiValue() {
    return (
        <span
            aria-label="Not available because it was not supplied by the API"
            className="attendance-command-missing-value"
            title="Not supplied by the API"
        >
            Not available
        </span>
    );
}

function renderApiValue(value: string | null | undefined) {
    return value?.trim() ? value : <MissingApiValue />;
}

function renderManagementTimestamp(value: string | null | undefined) {
    return value ? formatManagementTimestamp(value) : <MissingApiValue />;
}

function ManagementCommandHistoryFeedback(props: {
    error: IAttendanceRequestError | null;
    validationErrors: IAttendanceRequestFieldError[];
}) {
    useEffect(() => {
        if (props.error) {
            ServiceManager.ToastService.showError(props.error.message);
        }

        props.validationErrors.forEach((error) => {
            ServiceManager.ToastService.showError(error.message);
        });
    }, [props.error, props.validationErrors]);

    return null;
}

export default function CommandHistoryV2(props: IManagementCommandHistoryProps) {
    const applyTimeoutRef = useRef<number | undefined>(undefined);
    const [expandedDetail, setExpandedDetail] = useState<IExpandedCommandDetail | null>(null);
    const [fromDateInput, setFromDateInput] = useState(() => toUkDateFromIsoDate(props.filters.fromDate));
    const [toDateInput, setToDateInput] = useState(() => toUkDateFromIsoDate(props.filters.toDate));
    const totalPages = props.totalCount === null
        ? 1
        : Math.max(1, Math.ceil(props.totalCount / props.pageSize));
    const firstVisibleRecord = props.rows.length === 0
        ? 0
        : ((props.page - 1) * props.pageSize) + 1;
    const lastVisibleRecord = props.rows.length === 0
        ? 0
        : Math.min(props.page * props.pageSize, props.totalCount ?? props.rows.length);
    const hasPagination = props.totalCount !== null && totalPages > 1;

    useEffect(() => {
        return () => window.clearTimeout(applyTimeoutRef.current);
    }, []);

    useEffect(() => {
        setFromDateInput(toUkDateFromIsoDate(props.filters.fromDate));
    }, [props.filters.fromDate]);

    useEffect(() => {
        setToDateInput(toUkDateFromIsoDate(props.filters.toDate));
    }, [props.filters.toDate]);

    const updateFilters = (updates: Partial<ICommandHistoryFilterInput>) => {
        const filters = { ...props.filters, ...updates };
        const searchLength = filters.search.trim().length;
        const isPartialSearch = searchLength > 0 && searchLength < minimumSearchLength;
        const hasAppliedSearch = props.appliedFilters.search.trim().length >= minimumSearchLength;
        const filtersToApply = isPartialSearch ? { ...filters, search: "" } : filters;

        props.onFiltersChange(filters);
        window.clearTimeout(applyTimeoutRef.current);

        if (updates.search !== undefined && isPartialSearch && !hasAppliedSearch) {
            return;
        }

        applyTimeoutRef.current = window.setTimeout(() => props.onApply(filtersToApply), 300);
    };

    const updateFromDate = (value: string) => {
        setFromDateInput(value);
        const isoDate = toIsoDateFromUkDate(value);

        if (isoDate || value === "") {
            updateFilters({ fromDate: isoDate ?? "" });
        }
    };

    const updateToDate = (value: string) => {
        setToDateInput(value);
        const isoDate = toIsoDateFromUkDate(value);

        if (isoDate || value === "") {
            updateFilters({ toDate: isoDate ?? "" });
        }
    };

    const toggleDetail = (rowId: string, kind: CommandDetailKind) => {
        setExpandedDetail((currentDetail) => (
            currentDetail?.rowId === rowId && currentDetail.kind === kind
                ? null
                : { rowId, kind }
        ));
    };

    return (
        <div className="attendance-monitor-panel">
            <ManagementCommandHistoryFeedback
                error={props.error}
                validationErrors={props.validationErrors}
            />
            <div className="attendance-monitor-toolbar">
                <div className="attendance-monitor-filters attendance-command-history-filters">
                    <div className="attendance-command-history-primary-filters">
                        <div className="attendance-field attendance-command-history-date-filter">
                            <Form.Label htmlFor="command-date-from">Start date</Form.Label>
                            <AttendanceUkDateInput
                                disabled={props.isLoading}
                                id="command-date-from"
                                label="Start date"
                                onChange={updateFromDate}
                                placeholder="DD/MM/YYYY"
                                testId="attendance-command-history-from-date"
                                value={fromDateInput}
                            />
                        </div>
                        <div className="attendance-field attendance-command-history-date-filter">
                            <Form.Label htmlFor="command-date-to">End date</Form.Label>
                            <AttendanceUkDateInput
                                disabled={props.isLoading}
                                id="command-date-to"
                                label="End date"
                                onChange={updateToDate}
                                placeholder="DD/MM/YYYY"
                                testId="attendance-command-history-to-date"
                                value={toDateInput}
                            />
                        </div>
                        <div className="attendance-field attendance-command-history-class-filter">
                            <Form.Label htmlFor="command-class-filter">Class</Form.Label>
                            <Form.Control
                                aria-label="Class"
                                data-testid="attendance-command-history-class"
                                id="command-class-filter"
                                maxLength={50}
                                onChange={(event) => updateFilters({ class: event.target.value })}
                                type="text"
                                value={props.filters.class}
                            />
                        </div>
                        <div className="attendance-field attendance-command-history-status-filter">
                            <Form.Label htmlFor="command-status-filter">Status</Form.Label>
                            <Form.Select
                                aria-label="Status"
                                data-testid="attendance-command-history-status"
                                disabled={props.isLoading}
                                id="command-status-filter"
                                onChange={(event) => updateFilters({
                                    status: event.target.value as ICommandHistoryFilterInput["status"],
                                })}
                                value={props.filters.status}
                            >
                                <option value="">All statuses</option>
                                <option value="Queued">Queued</option>
                                <option value="Processing">Processing</option>
                                <option value="Completed">Completed</option>
                                <option value="Failed">Failed</option>
                                <option value="DeadLettered">Dead lettered</option>
                                <option value="Success">Success</option>
                            </Form.Select>
                        </div>
                        <div className="attendance-field attendance-command-history-student-filter">
                            <Form.Label htmlFor="command-search-filter">Search</Form.Label>
                            <Form.Control
                                aria-label="Search"
                                data-testid="attendance-command-history-search"
                                disabled={props.isLoading}
                                id="command-search-filter"
                                maxLength={100}
                                onChange={(event) => updateFilters({ search: event.target.value })}
                                type="search"
                                value={props.filters.search}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {props.isLoading ? (
                <div className="attendance-monitor-empty" role="status">Loading command history.</div>
            ) : (
                <>
                <Table responsive hover className="attendance-data-table attendance-command-history-table mb-0">
                    <caption className="visually-hidden">
                        {`Attendance management command history from ${props.appliedFilters.fromDate} through ${props.appliedFilters.toDate}.`}
                    </caption>
                    <thead>
                        <tr>
                            <th scope="col">Created</th>
                            <th scope="col">Student / MIS ID</th>
                            <th scope="col">Year / Class</th>
                            <th scope="col">Mark</th>
                            <th scope="col">Status</th>
                            <th scope="col">Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.rows.map((row) => {
                            const studentName = row.studentName?.trim();
                            const studentMisId = row.studentMisId?.trim();
                            const year = row.year?.trim();
                            const className = row.class?.trim();
                            const attendanceMark = row.attendanceMark?.trim();
                            const commandLabel = studentName ?? `command ${row.commandId}`;
                            const isResultExpanded = expandedDetail?.rowId === row.commandId
                                && expandedDetail.kind === "result";
                            const isMoreDetailsExpanded = expandedDetail?.rowId === row.commandId
                                && expandedDetail.kind === "more-details";

                            return (
                                <Fragment key={row.commandId}>
                                    <tr>
                                        <td>{renderManagementTimestamp(row.createdUtc)}</td>
                                        <td>
                                            {studentName ? <strong>{studentName}</strong> : <MissingApiValue />}
                                            <span className="attendance-student-mis-id">
                                                {studentMisId ?? <MissingApiValue />}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="attendance-student-year-class">
                                                <span>{year ?? <MissingApiValue />}</span>
                                                <span>{className ?? <MissingApiValue />}</span>
                                            </div>
                                        </td>
                                        <td>
                                            {attendanceMark
                                                ? <code className="attendance-code-token">{attendanceMark}</code>
                                                : <MissingApiValue />}
                                        </td>
                                        <td><AttendanceStatusChip status={row.status} /></td>
                                        <td>
                                            <div className="attendance-command-detail-actions">
                                                <MISTooltip message={`${isResultExpanded ? "Hide" : "View"} result`} placement="top">
                                                    <Button
                                                        aria-controls={`${row.commandId}-result`}
                                                        aria-expanded={isResultExpanded}
                                                        aria-label={`${isResultExpanded ? "Hide" : "View"} result for ${commandLabel}`}
                                                        className="attendance-command-detail-icon"
                                                        onClick={() => toggleDetail(row.commandId, "result")}
                                                        size="sm"
                                                        variant="outline-secondary"
                                                    >
                                                        <FontAwesomeIcon aria-hidden="true" icon={faCircleInfo} />
                                                    </Button>
                                                </MISTooltip>
                                                <MISTooltip message={`${isMoreDetailsExpanded ? "Hide" : "Show"} more details`} placement="top">
                                                    <Button
                                                        aria-controls={`${row.commandId}-more-details`}
                                                        aria-expanded={isMoreDetailsExpanded}
                                                        aria-label={`${isMoreDetailsExpanded ? "Hide" : "Show"} more details for ${commandLabel}`}
                                                        className="attendance-command-detail-icon"
                                                        onClick={() => toggleDetail(row.commandId, "more-details")}
                                                        size="sm"
                                                        variant="outline-secondary"
                                                    >
                                                        <FontAwesomeIcon aria-hidden="true" icon={isMoreDetailsExpanded ? faChevronUp : faChevronDown} />
                                                    </Button>
                                                </MISTooltip>
                                            </div>
                                        </td>
                                    </tr>
                                    {isResultExpanded && (
                                        <tr className="attendance-command-expanded-row">
                                            <td colSpan={6}>
                                                <section className="attendance-command-expanded-content" id={`${row.commandId}-result`}>
                                                    <div>
                                                        <span>Result code</span>
                                                        <strong>{renderApiValue(row.resultCode)}</strong>
                                                    </div>
                                                    <div>
                                                        <span>Result message</span>
                                                        <strong>{renderApiValue(row.resultMessage)}</strong>
                                                    </div>
                                                </section>
                                            </td>
                                        </tr>
                                    )}
                                    {isMoreDetailsExpanded && (
                                        <tr className="attendance-command-expanded-row">
                                            <td colSpan={6}>
                                                <section className="attendance-command-expanded-content attendance-command-more-details" id={`${row.commandId}-more-details`}>
                                                    <div>
                                                        <span>Command type</span>
                                                        <strong>{renderApiValue(row.commandType)}</strong>
                                                    </div>
                                                    <div>
                                                        <span>Processed</span>
                                                        <strong>{renderManagementTimestamp(row.processedUtc)}</strong>
                                                    </div>
                                                    <div>
                                                        <span>Target</span>
                                                        <strong>{renderApiValue(row.target)}</strong>
                                                    </div>
                                                    <div>
                                                        <span>Submitted by</span>
                                                        <strong>{renderApiValue(row.submittedBy)}</strong>
                                                    </div>
                                                    <div>
                                                        <span>Command ID</span>
                                                        <strong>{renderApiValue(row.commandId)}</strong>
                                                    </div>
                                                </section>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                        {props.rows.length === 0 && (
                            <tr><td className="attendance-monitor-empty" colSpan={6}>No command history is available for the selected filters.</td></tr>
                        )}
                    </tbody>
                </Table>
                    {hasPagination && (
                        <nav aria-label="Command history pagination" className="attendance-command-history-pagination">
                            <span aria-live="polite" className="attendance-command-history-pagination-summary">
                                {`Showing ${firstVisibleRecord}-${lastVisibleRecord} of ${props.totalCount}`}
                            </span>
                            <div className="attendance-command-history-pagination-actions">
                                <Button
                                    aria-label="Previous command history page"
                                    className="attendance-monitor-pagination-button"
                                    data-testid="attendance-command-history-previous-page"
                                    disabled={props.page <= 1}
                                    onClick={() => props.onPageChange(props.page - 1)}
                                    size="sm"
                                    title="Previous page"
                                    type="button"
                                    variant="outline-secondary"
                                >
                                    <FontAwesomeIcon aria-hidden="true" icon={faChevronLeft} />
                                </Button>
                                <span className="attendance-command-history-pagination-page">
                                    {`Page ${props.page} of ${totalPages}`}
                                </span>
                                <Button
                                    aria-label="Next command history page"
                                    className="attendance-monitor-pagination-button"
                                    data-testid="attendance-command-history-next-page"
                                    disabled={props.page >= totalPages}
                                    onClick={() => props.onPageChange(props.page + 1)}
                                    size="sm"
                                    title="Next page"
                                    type="button"
                                    variant="outline-secondary"
                                >
                                    <FontAwesomeIcon aria-hidden="true" icon={faChevronRight} />
                                </Button>
                            </div>
                        </nav>
                    )}
                </>
            )}
        </div>
    );
}